import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Label, EASE } from "./Reveal";
import { SAMPLE, SAMPLE_PDF } from "./content";
import { Download } from "lucide-react";

const TABS = [
  { id: "before", label: "Before", note: "Duty, not achievement. No verb, no result." },
  { id: "draft", label: "Our draft", note: "Strong structure. The numbers are questions we send you." },
  { id: "final", label: "Final", note: "Your real numbers in. Keywords matched to the target job." },
];

const STYLE = {
  verb: "font-semibold text-[var(--teal)]",
  gap: "mx-0.5 rounded border border-dashed border-[var(--amber)] px-1.5 text-[var(--amber)]",
  num: "rounded bg-[var(--amber)]/20 px-1 text-[var(--amber)]",
  kw: "underline decoration-[var(--teal)] decoration-2 underline-offset-4",
};

export const Sample = () => {
  const [tab, setTab] = useState("before");
  const current = TABS.find((t) => t.id === tab);
  return (
    <section id="sample" className="relative bg-[var(--navy)] py-28 sm:py-36" data-testid="sample-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <Label>Before & after</Label>
          <h2 className="font-serif-d mt-5 max-w-3xl text-5xl leading-[0.95] text-white sm:text-6xl">
            One bullet, <span className="italic text-[var(--teal)]">three passes.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-14 rounded-3xl border border-white/10 bg-[var(--ink)] p-6 sm:p-12">
          <div className="flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`sample-tab-${t.id}`}
                className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${tab === t.id ? "text-[var(--ink)]" : "text-slate-300 hover:text-white"}`}>
                {tab === t.id && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-[var(--teal)]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-10 grid min-h-[190px] sm:min-h-[150px]">
            <AnimatePresence initial={false}>
              <motion.p key={tab} data-testid="sample-bullet-text"
                initial={{ opacity: 0, y: 24, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -16, filter: "blur(6px)" }} transition={{ duration: 0.6, ease: EASE }}
                className={`col-start-1 row-start-1 text-2xl leading-snug sm:text-[2rem] ${tab === "before" ? "text-slate-400 line-through decoration-white/20" : "text-white"}`}>
                {SAMPLE[tab].map((s, i) => <span key={i} className={STYLE[s.k] || ""}>{s.t}</span>)}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-300" data-testid="sample-note">{current.note}</p>
            <p className="font-mono-d text-xs text-slate-400">Illustrative example</p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="mt-8 flex flex-col gap-5 rounded-3xl border border-[var(--teal)]/30 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div>
            <p className="font-serif-d text-3xl text-white">See a full before & after page.</p>
            <p className="mt-2 text-sm text-slate-300">A one-page sample PDF: original bullets, the rewrite, and what changed.</p>
          </div>
          <a href={SAMPLE_PDF} download data-testid="sample-pdf-download"
            className="group flex shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--teal)] px-6 py-3.5 font-semibold text-[var(--ink)] transition-transform duration-300 hover:scale-[1.03] active:scale-95">
            <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" /> Download sample PDF
          </a>
        </Reveal>
      </div>
    </section>
  );
};
